import { Component, h } from 'preact';
import { Localizer } from 'preact-i18n';
import * as appStyles from '../assets/global.scss';
import Icon from '../common/Icon/Icon';
import { getKey, Key, Properties } from '../common';
import { Row } from '../common/Layout/Layout';
import { withStyles, WithStyles } from '../style';
import { isFunction, isPresent, isRadio, stopPropagation } from '../utils';
import * as styles from './components.scss';
import { ModalContext } from './types';
import { FinText } from '../i18n';

export const MODAL_NO_INITIAL_FOCUS = 'bw-no-initial-focus';
export const MODAL_TITLE = 'bw-modal-title';

// == Modal ===============================================

export interface ModalProps extends Properties {
  wide?: boolean;
  // wider width mostly for histogram
  largeWide?: boolean;
  onClose?: () => void;
}

export interface ModalState {
  modalOffset?: { left: number };
}

@withStyles(styles)
export class Modal extends Component<ModalProps, ModalState> {
  static defaultProps: ModalProps = {
    wide: false,
    largeWide: false,
  };

  state: ModalState = {
    modalOffset: null,
  };

  private contentElem: HTMLElement;

  componentDidMount(): void {
    this.tryAutoFocus();
  }

  closeModal = (): void => {
    const { onClose } = this.props;

    if (onClose) {
      onClose();
    }

    (this.context as ModalContext).modal.hideModal();
  };

  render(
    { wide, largeWide, children, tx, ...attr }: ModalProps & WithStyles,
    { modalOffset }: ModalState
  ): h.JSX.Element {
    return (
      <div
        {...attr}
        class={tx(this.props.class, 'modal')}
        role="dialog"
        aria-labelledby={MODAL_TITLE}
        aria-modal="true"
        onKeyDown={this.onKeyDown}
        style={modalOffset}
      >
        <div class={tx('backdrop', 'grow--pos fill--pos fixed')} onClick={this.closeModal} />
        <div class={tx('bw-modal-canary-pre', 'aria-hidden')} tabIndex={0} onFocus={this.focusLast} />
        <div
          class={tx('bw-content', 'white-bg fill', { wide, largeWide })}
          ref={(elem: HTMLElement) => (this.contentElem = elem)}
        >
          <div class={tx('contentLayout', 'flex flex--column fill')}>{children}</div>
        </div>
        <div class={tx('bw-modal-canary-post', 'aria-hidden')} tabIndex={0} onFocus={this.focusFirst} />
      </div>
    );
  }

  // == Focus and Event handling ==

  private focusFirst = (e: Event): void => {
    stopPropagation(e);
    this.elementList().find(this.tryToFocus);
  };

  private focusLast = (e: Event): void => {
    stopPropagation(e);
    this.elementList().reverse().find(this.tryToFocus);
  };

  private onKeyDown = (e: KeyboardEvent): void => {
    if (getKey(e) === Key.ESC) {
      stopPropagation(e);
      this.closeModal();
    }
  };

  // == Focus utilities ==

  private elementList = (): HTMLElement[] =>
    isPresent(this.contentElem) ? (Array.from(this.contentElem.getElementsByTagName('*')) as HTMLElement[]) : [];

  private tryToFocus = (elem: HTMLElement): boolean => {
    if (!isFunction(elem.focus)) {
      return false;
    }

    elem.focus();
    const focused: boolean = document.activeElement === elem;
    // If focused element is radio then try to focus the selected input
    if (focused && isRadio(elem)) {
      const selected: HTMLInputElement = this.contentElem.querySelector(`input[name="${elem.name}"][selected="true"]`);
      if (isPresent(selected)) {
        selected.focus();
      }
    }
    return focused;
  };

  private tryAutoFocus(): void {
    // Initial focus: autofocus -> find element with autofocus attribute, if not found focus on close button
    const autoFocusElem: HTMLElement = this.contentElem.querySelector('[autofocus]');
    if (document.activeElement !== autoFocusElem && !this.contentElem.contains(document.activeElement)) {
      if (isPresent(autoFocusElem)) {
        autoFocusElem.focus();
      } else {
        const closeButton: HTMLElement = this.contentElem.querySelector('.bw-modal-close');
        closeButton.focus();
      }
    }
  }
}

// == ModalHeader =========================================

export interface ModalHeaderProps extends Properties {
  compact?: boolean;
}

@withStyles(styles)
export class ModalHeader extends Component<ModalHeaderProps> {
  render({ children, tx, ...attr }: ModalHeaderProps & WithStyles): h.JSX.Element {
    return (
      <div
        {...attr}
        class={tx(this.props.class, 'header', 'flex--nogrow white-bg fill', {
          'ps-large': !this.props.compact,
          'ps-large-x': this.props.compact,
          'ps-xsmall-y': this.props.compact,
          compact: this.props.compact,
        })}
      >
        {children}
      </div>
    );
  }
}

// == ModalClose ==========================================

export interface ModalCloseProps extends Properties {
  onClose?: () => void;
}
@withStyles(styles, appStyles)
export class ModalClose extends Component<ModalCloseProps> {
  closeModal = (): void => {
    const { onClose } = this.props;

    if (onClose) {
      onClose();
    }

    (this.context as ModalContext).modal.hideModal();
  };

  render({ tx, ...attr }: ModalCloseProps & WithStyles): h.JSX.Element {
    return (
      <Localizer>
        <button
          aria-label={<FinText id="action.close" />}
          class={tx(this.props.class, 'bw-modal-close', MODAL_NO_INITIAL_FOCUS, 'focusInset')}
          onClick={this.closeModal}
          {...attr}
        >
          <Icon iconName="close-delete" class={tx('is-small')} />
        </button>
      </Localizer>
    );
  }
}

// == ModalTitle ==========================================

export interface ModalTitleProps extends Properties {
  onClose?: () => void;
}
@withStyles(styles, appStyles)
export class ModalTitle extends Component<ModalTitleProps> {
  render({ children, tx, onClose, ...attr }: ModalTitleProps & WithStyles): h.JSX.Element {
    return (
      <Row {...attr} class={tx(this.props.class)} grow={false}>
        <div class="flex-1 nordic-blue-900-text font-heading-5" id={MODAL_TITLE}>
          {children}
        </div>
        <ModalClose onClose={onClose} />
      </Row>
    );
  }
}

// == ModalBody ===========================================

export interface ModalBodyProps extends Properties {}

@withStyles(styles)
export class ModalBody extends Component<ModalBodyProps> {
  render({ children, tx, ...attr }: ModalBodyProps & WithStyles): h.JSX.Element {
    return (
      <div {...attr} class={tx(this.props.class, 'body', 'flex-1 white-bg fill ps-xsmall-b')}>
        {children}
      </div>
    );
  }
}

// == ModalFooter =========================================

export interface ModalFooterProps extends Properties {
  minWidth?: boolean;
  sticky?: boolean;
  cta?: boolean;
  compact?: boolean;
}

@withStyles(styles)
export class ModalFooter extends Component<ModalFooterProps> {
  render({ sticky, children, tx, ...attr }: ModalFooterProps & WithStyles): h.JSX.Element {
    return (
      <Row
        {...attr}
        class={tx(this.props.class, 'footer', 'flex--nogrow  grey-50-bg fill flex--center', {
          sticky,
          'ps-small-y': !this.props.compact,
          'ps-xsmall-y': this.props.compact,
          compact: this.props.compact,
        })}
        grow={false}
      >
        {children}
      </Row>
    );
  }
}

// == ModalButton =========================================

export interface ModalButtonProps extends Properties {
  minWidth?: boolean;
  cta?: boolean;
}

@withStyles(styles, appStyles)
export class ModalButton extends Component<ModalFooterProps> {
  static defaultProps: ModalButtonProps = { minWidth: true };

  render({ children, tx, minWidth, cta, ...attr }: ModalFooterProps & WithStyles): h.JSX.Element {
    return (
      <button
        {...attr}
        class={tx(
          this.props.class,
          'bw-modal-done',
          { minWidth },
          'ps-small-x ps-xsmall-y',
          'font-body-1 bold-type',
          {
            highButton: cta,
            lightButton: !cta,
          },
          'flex--nogrow'
        )}
      >
        {children}
      </button>
    );
  }
}
